//Forest view status bar

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { RootState } from '../../store';
import { setShowTreeBubbles, setShowUserBubbles } from '../../store/slices/uiSlice';
import Button from '../common/Button';
import { ASSET_PATHS } from '../../utils/gameConfig';
import { activityManager } from '../../services/activityManager';

//Props interface
interface StatusBarProps {
  onExerciseClick: () => void;
  exerciseButtonState: {
    currentDay: number;
    available: boolean;
    canDo: boolean;
    completed: boolean;
    isDisabled: boolean;
    buttonText: string;
    tooltipText: string;
  };
  exerciseButtonTooltip: string;
}

const StatusBar: React.FC<StatusBarProps> = ({
  onExerciseClick,
  exerciseButtonState,
  exerciseButtonTooltip
}) => {
  const dispatch = useDispatch();
  
  //Hooks
  const { user } = useSelector((state: RootState) => state.auth);
  const { showTreeBubbles, showUserBubbles } = useSelector((state: RootState) => state.ui);
  const { currentDay, hasCompletedDailyExercise } = useSelector((state: RootState) => state.game);
  const { currentForest } = useSelector((state: RootState) => state.forest) || {};
  
  const [waterReceivedFlash, setWaterReceivedFlash] = useState(false);
  const [totalWaterReceived, setTotalWaterReceived] = useState(0);


  //Water polling

  useEffect(() => {
  if (!user || !currentForest) {
    setTotalWaterReceived(0);
    return;
  }

  let intervalId: NodeJS.Timeout | null = null;
  let activityCheckId: NodeJS.Timeout | null = null;

  const fetchWaterReceived = async () => {
    try {
      console.log('Fetching water stats for active user');
      const { getUserWaterActions } = await import('../../services/firestore');
      const waterActions = await getUserWaterActions(currentForest.id, user.uid);
      
      const totalReceived = waterActions.filter(action => action.fromUserId !== user.uid).length;
      setTotalWaterReceived(totalReceived);
      
    } catch (error) {
      console.error('Error fetching water received:', error);
      setTotalWaterReceived(0);
    }
  };

  //Initial fetch
  //fetchWaterReceived();

  const startPolling = () => {
    if (intervalId) return;
    console.log('Starting StatusBar water polling');
    fetchWaterReceived();
    intervalId = setInterval(fetchWaterReceived, 10000);
  };

  //let intervalId: NodeJS.Timeout | null = null;

  const stopPolling = () => {
    if (!intervalId) return;
    console.log('Stopping StatusBar water polling');
    clearInterval(intervalId);
    intervalId = null;
  };

  /*
  if (activityManager.isActive()) {
    intervalId = startPolling();
  }
  */

  if (activityManager.isActive()) {
    startPolling();
  } else {
    console.log('User inactive on mount, no polling started');
  }

  activityCheckId = setInterval(() => {
    const isUserActive = activityManager.isActive();
    const hasActivePolling = intervalId !== null;

    if (isUserActive && !hasActivePolling) {
      console.log('User became active, starting StatusBar polling');
      startPolling();
    } else if (!isUserActive && hasActivePolling) {
      console.log('User became inactive, stopping StatusBar polling');
      stopPolling();
    }
  }, 30000);

  //Listener
  /*
  const handleWaterStatsUpdate = (event: CustomEvent) => {
    const { userId, forestId, totalReceived } = event.detail;
    if (userId === user.uid && forestId === currentForest.id) {
      setTotalWaterReceived(totalReceived);
      console.log(`Water stats updated: ${totalReceived}`);
    }
  };*/

  const handleWaterStatsUpdate = (event: CustomEvent) => {
    const { userId, forestId, totalReceived } = event.detail;
    if (user && currentForest && userId === user.uid && forestId === currentForest.id) {
      setTotalWaterReceived(totalReceived);
      console.log(`Water stats updated via per-forest monitor: ${totalReceived}`);
    }
  };

  window.addEventListener('waterStatsUpdate', handleWaterStatsUpdate as EventListener);
  
  return () => {
    console.log('StatusBar cleanup: stopping all polling');
    if (intervalId) {
      clearInterval(intervalId);
    }
    if (activityCheckId) {
      clearInterval(activityCheckId);
    }
    window.removeEventListener('waterStatsUpdate', handleWaterStatsUpdate as EventListener);
  };
}, [user, currentForest]);


  //Use Props (may change back to exercise hook everntually?)
  const handleDailyExercise = () => {
    console.log('Opening daily exercise...');
    onExerciseClick();
  };

  const handleChatbot = () => {
    console.log('Opening chatbot...');
    //ToDo: Open chatbot modal.  Need chatbot code.
  };

  const handleBackToProfile = () => {
    console.log('Returning to profile...');
    window.location.hash = '';
  };

  const handleWaterReceivedClick = () => {
    console.log('Viewing water received history...');
    //ToDo: Implement water received history list
    setWaterReceivedFlash(false);
  };

  const toggleTreeBubbles = () => {
    dispatch(setShowTreeBubbles(!showTreeBubbles));
    //ToDo: Build this
  };

  const toggleUserBubbles = () => {
    dispatch(setShowUserBubbles(!showUserBubbles));
    //ToDo: Build this
  };

  return (
    <div className="h-full flex items-center justify-between px-6 py-3">
      {/* Left Section - User Info */}
      <div className="flex items-center space-x-6">
        {/* User Initials */}
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-sage-500 rounded-full flex items-center justify-center text-white font-bold">
            {user?.initials}
          </div>
          {/* Show current exercise day */}
          <span className="text-white font-medium">Day {exerciseButtonState.currentDay}</span>
        </div>

        {/* Stats */}
        <div className="flex items-center space-x-4">
          {/* Seeds */}
          <div className="flex items-center space-x-2 bg-white/20 rounded-lg px-3 py-2">
            <img 
              src={ASSET_PATHS.ICONS.SEED} 
              alt="Seeds" 
              className="w-5 h-5"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling!.textContent = '🌱 ' + target.nextElementSibling!.textContent;
              }}
            />
            <span className="text-white font-medium">{user?.seeds}</span>
          </div>

          {/* Water Available */}
          <div className="flex items-center space-x-2 bg-white/20 rounded-lg px-3 py-2">
            <img 
              src={ASSET_PATHS.ICONS.WATER} 
              alt="Water" 
              className="w-5 h-5"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling!.textContent = '💧 ' + target.nextElementSibling!.textContent;
              }}
            />
            <span className="text-white font-medium">{user?.water}</span>
          </div>

          {/* Water Received */}
          <motion.div 
            className={`flex items-center space-x-2 bg-white/20 rounded-lg px-3 py-2 cursor-pointer ${
              waterReceivedFlash ? 'bg-red-500/60' : ''
            }`}
            onClick={handleWaterReceivedClick}
            animate={waterReceivedFlash ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 0.5 }}
          >
            <span className="text-white text-sm">Water Received:</span>
            <span className="text-white font-medium">{totalWaterReceived}</span>
          </motion.div>
        </div>
      </div>

      {/* Right Section - Action Buttons */}
      <div className="flex items-center space-x-3">
        {/* Daily Exercise */}
        <Button
          variant={exerciseButtonState.completed ? "outline" : "primary"}
          size="sm"
          onClick={handleDailyExercise}
          disabled={exerciseButtonState.isDisabled}
        >
          <div className="flex items-center space-x-2">
            <img 
              src={ASSET_PATHS.ICONS.EXERCISE} 
              alt="Exercise" 
              className="w-4 h-4"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <span>{exerciseButtonState.buttonText}</span>
          </div>
        </Button>

        {/* Chatbot */}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleChatbot}
        >
          <div className="flex items-center space-x-2">
            <img 
              src={ASSET_PATHS.ICONS.CHAT} 
              alt="Chat" 
              className="w-4 h-4"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <span>Chat</span>
          </div>
        </Button>

        {/* Toggle Buttons */}
        <div className="flex items-center space-x-1">
          <button
            onClick={toggleTreeBubbles}
            className={`p-2 rounded transition-colors ${
              showTreeBubbles ? 'bg-sage-500' : 'bg-white/20'
            }`}
            title="Toggle tree owner bubbles"
          >
            <span className="text-white text-sm">🌳</span>
          </button>
          
          <button
            onClick={toggleUserBubbles}
            className={`p-2 rounded transition-colors ${
              showUserBubbles ? 'bg-sage-500' : 'bg-white/20'
            }`}
            title="Toggle user name bubbles"
          >
            <span className="text-white text-sm">👥</span>
          </button>
        </div>

        {/* Back to Profile */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleBackToProfile}
        >
          <div className="flex items-center space-x-2">
            <img 
              src={ASSET_PATHS.ICONS.PROFILE} 
              alt="Profile" 
              className="w-4 h-4"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <span>Profile</span>
          </div>
        </Button>
      </div>
    </div>
  );
};

export default StatusBar;